import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { YouTubePlayerModule } from '@angular/youtube-player';
import { ConsentService, SafeCheckPipe, WindowRef } from '@boels/helpers';
import {
  AlignmentEnum,
  BackgroundColorEnum,
  BoelsWindow,
  CmsComponentData,
  YoutubeVideoComponentData,
} from '@boels/models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faPlay,
  faSquareArrowUpRight,
} from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'boels-youtube-video',
  templateUrl: './youtube-video.component.html',
  styleUrls: ['./youtube-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { ngSkipHydration: 'true' },
  imports: [
    CommonModule,
    SafeCheckPipe,
    TranslocoModule,
    YouTubePlayerModule,
    FontAwesomeModule,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['youtube'],
    },
  ],
})
export class YoutubeVideoComponent implements OnInit {
  protected winRef = inject(WindowRef);
  private renderer = inject(Renderer2);
  private consentService = inject(ConsentService);

  @ViewChild('player') player: ElementRef<HTMLElement>;
  @ViewChild('playerContainer') playerContainer: ElementRef<HTMLDivElement>;

  @Input() componentData: CmsComponentData<YoutubeVideoComponentData>;

  notPlayedYet$ = new BehaviorSubject(true);
  backgroundColor = BackgroundColorEnum;
  alignment = AlignmentEnum;
  youtubeApiLoaded = false;

  faPlay = faPlay;
  faArrow = faSquareArrowUpRight;

  width: number;

  ngOnInit(): void {
    if (!this.youtubeApiLoaded) {
      this.addYoutubeScript();
      this.youtubeApiLoaded = true;
    }

    // Temporary fix for Youtube component not being responsive
    // TODO: Remove once https://github.com/angular/components/issues/28829 is resolved
    if (this.isMobileDevice()) {
      this.setVideoWidth();
    }
  }

  // Temporary fix for Youtube component not being responsive
  // TODO: Remove once https://github.com/angular/components/issues/28829 is resolved
  isMobileDevice(): boolean {
    const userAgent = this.winRef.nativeWindow?.navigator.userAgent;
    if (userAgent) {
      return /Mobi/.test(userAgent);
    }
    return false;
  }

  // Temporary fix for Youtube component not being responsive
  // TODO: Remove once https://github.com/angular/components/issues/28829 is resolved
  setVideoWidth(): void {
    this.width = this.winRef.nativeWindow?.innerWidth ?? 0;
  }

  play(): void {
    this.notPlayedYet$.next(false);
  }

  onPlayerReady(event: YT.PlayerEvent): void {
    event.target.playVideo();
  }

  addYoutubeScript(): void {
    const script = this.renderer.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    this.renderer.appendChild(this.winRef.document.body, script);
  }

  isTextLessVideo(component: YoutubeVideoComponentData): boolean {
    return component.imageAlignment === this.alignment.CENTER;
  }

  cookieAccepted$() {
    return this.consentService.hasConsentFor$('preferences');
  }

  openCookiebotModal(event: Event): void {
    event.preventDefault();
    const cookieBot = (this.winRef.nativeWindow as BoelsWindow).CookieConsent;
    cookieBot?.show();
  }
}
